import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, poundsFromPence, BACKEND_URL, getToken } from "../lib/api";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import { Download, MousePointer2, Megaphone, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

const RETURN_COLORS = ["#dc2626", "#ef6a4c", "#e0a458", "#6d6a86"];
const RETURN_DIST = (f) => {
  const d = f.returns?.visit_dist || {};
  return [
    { label: "1 visit", value: d["1"] || 0 },
    { label: "2 visits", value: d["2"] || 0 },
    { label: "3 visits", value: d["3"] || 0 },
    { label: "4+ visits", value: d["4+"] || 0 },
  ];
};

export default function ReportsPage() {
  const { currentEventId, currentEvent } = useEvent();
  const [searchParams] = useSearchParams();
  const focusSource = searchParams.get("source") || "";
  const focusCampaign = searchParams.get("campaign") || "";
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (start) params.set("start", start); if (end) params.set("end", end);
    if (currentEventId) params.set("event_id", currentEventId);
    const r = await api.get(`/reports?${params.toString()}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [start, end, currentEventId]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (start) params.set("start", start); if (end) params.set("end", end);
    if (currentEventId) params.set("event_id", currentEventId);
    // CSV endpoint needs auth header, so fetch + download blob
    const res = await fetch(`${BACKEND_URL}/api/reports/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "orders.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout title="Reports" subtitle={currentEvent ? `${currentEvent.name} · sales, commission and breakdowns.` : "Sales, commission and breakdowns."}
      actions={<button className="st-btn" onClick={exportCsv} data-testid="export-csv"><Download size={14}/> Export CSV</button>}>
      <div className="st-card p-4 flex gap-3 mb-6 items-end flex-wrap">
        {currentEvent && (
          <div className="mr-auto" data-testid="reports-event-scope">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#7a7a7a]">Showing data for</span>
            <div className="text-sm font-semibold text-[#dc2626]">{currentEvent.name}</div>
          </div>
        )}
        <div>
          <label className="text-xs text-[#8a8a8a]">Start</label>
          <input type="date" className="st-input mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rep-start" />
        </div>
        <div>
          <label className="text-xs text-[#8a8a8a]">End</label>
          <input type="date" className="st-input mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rep-end" />
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Gross" value={poundsFromPence(data.gross_pence)} />
            <Stat label="Commission" value={poundsFromPence(data.commission_pence)} />
            <Stat label="Net" value={poundsFromPence(data.net_pence)} />
            <Stat label="Orders" value={data.orders_count} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="st-card p-4">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">By ticket type</h3>
              <table className="w-full text-sm">
                <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
                  <tr><th className="py-2">Type</th><th>Qty</th></tr>
                </thead>
                <tbody data-testid="rep-by-type">
                  {data.by_type.length === 0 && <tr><td colSpan={2} className="py-4 text-[#8a8a8a]">No data</td></tr>}
                  {data.by_type.map(r => <tr key={r.name} className="border-t border-[#1a1a1a]"><td className="py-2">{r.name}</td><td className="font-semibold">{r.qty}</td></tr>)}
                </tbody>
              </table>
            </div>

            <div className="st-card p-4">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">By date</h3>
              <BarSeries data={data.by_date} />
            </div>
          </div>

          <TrafficAttribution eventId={currentEventId} focusSource={focusSource} focusCampaign={focusCampaign} />
        </>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="st-card p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

function BarSeries({ data }) {
  if (!data?.length) return <p className="text-[#8a8a8a] text-sm">No data</p>;
  const max = Math.max(1, ...data.map(d => d.total_pence));
  return (
    <div className="space-y-2" data-testid="rep-by-date">
      {data.map(d => (
        <div key={d.date} className="flex items-center gap-3">
          <div className="text-xs text-[#8a8a8a] w-20">{d.date}</div>
          <div className="flex-1 h-3 bg-[#161616] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#dc2626] to-[#7f1d1d]" style={{ width: `${(d.total_pence / max) * 100}%` }} />
          </div>
          <div className="text-xs font-semibold w-20 text-right">{poundsFromPence(d.total_pence)}</div>
        </div>
      ))}
    </div>
  );
}

const FUNNEL_STAGES = [
  { key: "visited", label: "Visits" },
  { key: "selected", label: "Selected" },
  { key: "checkout", label: "Checkout" },
  { key: "paid", label: "Paid" },
];

// Compact 4-stage bar row (Visits -> Selected -> Checkout -> Paid), bars sized to this row's visits.
function MiniFunnel({ row }) {
  const base = Math.max(1, row.visited || 0);
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="mini-funnel">
      {FUNNEL_STAGES.map((st) => {
        const v = row[st.key] || 0;
        return (
          <div key={st.key} className="min-w-0">
            <div className="flex items-baseline justify-between gap-1">
              <span className="text-[9px] uppercase tracking-wider text-[#6a6a6a] truncate">{st.label}</span>
              <span className="text-xs font-bold tabular-nums">{v}</span>
            </div>
            <div className="mt-0.5 h-1.5 bg-[#161616] rounded-full overflow-hidden">
              <div className="h-full bg-[#dc2626]" style={{ width: `${Math.round((v / base) * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#7a7a7a]">
        <Icon size={12} className="text-[#dc2626]" /> {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub ? <div className="text-[11px] text-[#7a7a7a] mt-0.5">{sub}</div> : null}
    </div>
  );
}

// Detailed marketing-attribution section: traffic sources + campaign drill-down with full V->S->C->P funnel.
// How long people mull it over: elapsed time from first visit to purchase.
function DaysToBook({ dtb }) {
  const dist = dtb?.dist || {};
  const buckets = [
    { key: "same_day", label: "Same day" },
    { key: "1-2 days", label: "1–2 days" },
    { key: "3-7 days", label: "3–7 days" },
    { key: "8+ days", label: "8+ days" },
  ];
  const total = Math.max(1, buckets.reduce((s, b) => s + (dist[b.key] || 0), 0));
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-4 mt-6" data-testid="reports-days-to-book">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">Days to book</div>
        <div className="text-[11px] text-[#7a7a7a]">how long people mull it over</div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex items-center gap-5 shrink-0">
          <div>
            <div className="text-3xl font-bold text-[#dc2626] leading-none">{dtb?.avg_days ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mt-1">avg days</div>
          </div>
          <div className="h-9 w-px bg-[#1e1e1e]" />
          <div>
            <div className="text-3xl font-bold leading-none">{dtb?.median_days ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mt-1">median days</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="space-y-2">
            {buckets.map((b) => {
              const v = dist[b.key] || 0;
              return (
                <div key={b.key} className="flex items-center gap-3" data-testid={`dtb-${b.key}`}>
                  <span className="text-xs text-[#8a8a8a] w-20 shrink-0">{b.label}</span>
                  <div className="flex-1 h-2 bg-[#161616] rounded-full overflow-hidden">
                    <div className="h-full bg-[#dc2626]" style={{ width: `${Math.round((v / total) * 100)}%` }} />
                  </div>
                  <span className="text-xs font-bold tabular-nums w-8 text-right">{v}</span>
                </div>
              );
            })}
          </div>
          {!dtb?.sample ? <div className="text-[11px] text-[#6a6a6a] mt-2">Populates once bookings come in.</div> : null}
        </div>
      </div>
    </div>
  );
}


function TrafficAttribution({ eventId, focusSource = "", focusCampaign = "" }) {
  const RANGES = [["7", "7d"], ["30", "30d"], ["90", "90d"], ["all", "All"]];
  const [days, setDays] = useState("30");
  const [attr, setAttr] = useState("first");
  const [srcFilter, setSrcFilter] = useState(focusSource || null);
  const [f, setF] = useState(null);
  const rootRef = React.useRef(null);

  useEffect(() => {
    const qs = `days=${days}&attr=${attr}${eventId ? `&event_id=${eventId}` : ""}`;
    api.get(`/dashboard/funnel?${qs}`).then((r) => setF(r.data)).catch(() => setF(null));
  }, [days, attr, eventId]);

  // When arriving from a saved-link "Report" button, widen the range, pre-filter the source and scroll here.
  useEffect(() => {
    if (focusSource) { setSrcFilter(focusSource); setDays("all"); }
    if (focusSource || focusCampaign) {
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [focusSource, focusCampaign]);

  const sources = f?.sources || [];
  const campaigns = (f?.campaigns || []).filter((c) => !srcFilter || c.source === srcFilter);
  const hasData = (f?.visited || 0) > 0;

  return (
    <div ref={rootRef} className="st-card p-4 mt-6 scroll-mt-6" data-testid="traffic-attribution">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] flex items-center gap-2">
            <Megaphone size={15} className="text-[#dc2626]" /> Marketing attribution
          </h3>
          <p className="text-xs text-[#7a7a7a] mt-1">Where your booking-page visitors came from, and how far each source & campaign got.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5" data-testid="attr-touch">
            {[["first", "First touch"], ["last", "Last touch"]].map(([val, lbl]) => (
              <button key={val} onClick={() => setAttr(val)} data-testid={`attr-touch-${val}`}
                      title={val === "first" ? "What first discovered the visitor" : "What last referred them before booking"}
                      className={`px-2.5 py-1 rounded-[3px] text-[11px] transition-colors ${attr === val ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5" data-testid="attr-range">
            {RANGES.map(([val, lbl]) => (
              <button key={val} onClick={() => setDays(val)} data-testid={`attr-range-${val}`}
                      className={`px-2.5 py-1 rounded-[3px] text-xs transition-colors ${days === val ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="text-center py-10 border border-dashed border-[#1e1e1e] rounded-xl" data-testid="attr-empty">
          <MousePointer2 size={22} className="text-[#3a3a3a] mx-auto mb-2" />
          <div className="text-sm text-[#8a8a8a]">No visits tracked in this range yet.</div>
          <div className="text-xs text-[#6a6a6a] mt-1">Tagged links (UTM) and cross-domain traffic will appear here as visitors browse your booking page.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <KpiTile icon={MousePointer2} label="Visitors" value={f.visited} sub={`${f.selected} selected tickets`} />
            <KpiTile icon={TrendingUp} label="Conversion" value={`${f.conversion_pct}%`} sub={`${f.paid} paid`} />
            <KpiTile icon={Megaphone} label="Sources" value={sources.length} sub="traffic channels" />
            <KpiTile icon={Megaphone} label="Campaigns" value={(f.campaigns || []).length} sub="tagged links" />
          </div>

          {/* Return behaviour */}
          <div className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-4 mb-5" data-testid="reports-returns">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">Return behaviour</div>
              <div className="text-[11px] text-[#7a7a7a]"><span className="text-[#dc2626] font-semibold">{f.returns?.repeat_rate || 0}%</span> come back more than once</div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="relative shrink-0 mx-auto sm:mx-0" style={{ width: 120, height: 120 }} data-testid="returns-dial">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={RETURN_DIST(f)} dataKey="value" nameKey="label" cx="50%" cy="50%"
                         innerRadius={42} outerRadius={57} paddingAngle={2} startAngle={90} endAngle={-270} stroke="none">
                      {RETURN_DIST(f).map((_, i) => <Cell key={i} fill={RETURN_COLORS[i]} />)}
                    </Pie>
                    <RTooltip contentStyle={{ background: "#0e0e0e", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }}
                              itemStyle={{ color: "#fff" }} formatter={(v, n) => [`${v} visitor${v === 1 ? "" : "s"}`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-bold text-[#dc2626] leading-none">{f.returns?.avg_visits_to_book || 0}</span>
                  <span className="text-[9px] uppercase tracking-wider text-[#7a7a7a] mt-1 text-center leading-tight">avg visits<br/>to book</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-2">How many visits before they commit</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-sm">
                  {RETURN_DIST(f).map((d, i) => {
                    const total = Math.max(1, RETURN_DIST(f).reduce((s, x) => s + x.value, 0));
                    return (
                      <div key={d.label} className="flex items-center gap-2" data-testid={`returns-dist-${d.label}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: RETURN_COLORS[i] }} />
                        <span className="text-xs text-[#c9c9c9] flex-1 truncate">{d.label}</span>
                        <span className="text-xs font-bold tabular-nums">{d.value}</span>
                        <span className="text-[10px] text-[#7a7a7a] w-9 text-right">{Math.round((d.value / total) * 100)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Traffic sources — full 6-step funnel per source */}
          <div className="mb-2 text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">
            Funnel by source <span className="text-[#5a5a5a] normal-case tracking-normal">· see where each source drops off · click a row for its campaigns</span>
          </div>
          <div className="overflow-x-auto rounded-xl border border-[#1a1a1a]" data-testid="attr-sources">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a] bg-[#0e0e0e]">
                <tr>
                  <th className="py-2.5 px-3">Source</th>
                  <th className="px-2 text-right">Landed</th>
                  <th className="px-2 text-right">Date</th>
                  {f.slotted ? <th className="px-2 text-right">Slot</th> : null}
                  <th className="px-2 text-right">Tickets</th>
                  <th className="px-2 text-right">Checkout</th>
                  <th className="px-2 text-right">Paid</th>
                  <th className="px-2 text-right">Conv %</th>
                  <th className="px-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => {
                  const active = srcFilter === s.source;
                  return (
                    <tr key={s.source}
                        onClick={() => setSrcFilter(active ? null : s.source)}
                        className={`border-t border-[#1a1a1a] cursor-pointer transition-colors ${active ? "bg-[#1a0d0d]" : "hover:bg-[#121212]"}`}
                        data-testid={`attr-source-${s.source}`}>
                      <td className="py-2.5 px-3 font-semibold capitalize">
                        <span className={active ? "text-[#dc2626]" : ""}>{s.source}</span>
                      </td>
                      <td className="px-2 text-right tabular-nums">{s.visited}</td>
                      <td className="px-2 text-right tabular-nums text-[#c9c9c9]">{s.date}</td>
                      {f.slotted ? <td className="px-2 text-right tabular-nums text-[#c9c9c9]">{s.slot}</td> : null}
                      <td className="px-2 text-right tabular-nums text-[#c9c9c9]">{s.tickets}</td>
                      <td className="px-2 text-right tabular-nums text-[#c9c9c9]">{s.checkout}</td>
                      <td className="px-2 text-right tabular-nums">{s.paid}</td>
                      <td className="px-2 text-right tabular-nums font-semibold text-[#dc2626]">{s.conversion_pct}%</td>
                      <td className="px-3 text-right font-semibold">{poundsFromPence(s.revenue_pence || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Days to book */}
          <DaysToBook dtb={f.days_to_book} />

          {/* Campaign drill-down */}
          <div className="mt-6 flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">
              Campaigns{srcFilter ? <span className="capitalize text-[#dc2626]"> · {srcFilter}</span> : ""}
            </div>
            {srcFilter && (
              <button onClick={() => setSrcFilter(null)} className="text-[11px] text-[#dc2626] hover:underline" data-testid="attr-campaign-clear">
                Clear filter
              </button>
            )}
          </div>
          {campaigns.length === 0 ? (
            <div className="text-xs text-[#7a7a7a] border border-dashed border-[#1e1e1e] rounded-xl p-4" data-testid="attr-campaigns-empty">
              {srcFilter ? `No campaign-tagged links for ${srcFilter}.` : "No campaign-tagged (UTM) links tracked yet. Build tagged links in Marketing → UTM builder."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="attr-campaigns">
              {campaigns.map((c) => (
                <div key={`${c.source}-${c.campaign}`}
                     className={`rounded-xl bg-[#0f0f0f] border p-3 ${focusCampaign && c.campaign === focusCampaign ? "border-[#dc2626]" : "border-[#1e1e1e]"}`}
                     data-testid={`attr-campaign-${c.campaign}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{c.campaign}</div>
                      <div className="text-[10px] text-[#7a7a7a] capitalize">{c.source} · {c.conversion_pct}% conversion</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-[#dc2626]">{poundsFromPence(c.revenue_pence || 0)}</div>
                      <div className="text-[10px] text-[#7a7a7a]">revenue</div>
                    </div>
                  </div>
                  <MiniFunnel row={c} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
